/**
 * The profile menu — roadmap 10.2, §1.4.
 *
 * **This is the role axis.** *Who am I acting as*: learner, instructor,
 * institution admin, organizer, ops. The product axis — *what am I browsing* —
 * is `ProductNav`, and the two answer different questions, so they never share
 * a control.
 *
 * Since Phase 10 the role surfaces live here and nowhere else. Most users hold
 * exactly one role and never need the list; the ones who hold three will look
 * in the profile menu for it, which is where every other product on the web
 * puts it. It also buys back the header width the old inline switcher cost.
 *
 * Order is deliberate: identity, then profile, then the surfaces held, then
 * dark mode and language, then sign out. Sign-out is last and separated — it is the one
 * destructive item, and it must not sit under a moving list where a mis-click
 * lands on it.
 *
 * Signing out clears the whole query cache (see `useLogout`), so the next person
 * to use this browser cannot see a frame of the previous user's data.
 */
import { KeyRound, LogOut, Moon, Settings, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router'

import { PORTALS } from '@/app/portals'
import { useLogout } from '@/shared/auth/hooks'
import { useSession } from '@/shared/auth/useSession'
import { useTheme } from '@/shared/theme'
import {
  Button,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui'

import { LanguageSwitcher } from './LanguageSwitcher'

export function AccountMenu() {
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')
  const session = useSession()
  const logout = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()

  if (session.status === 'loading') return null

  if (session.status === 'anonymous') {
    return (
      // The one wine button in the header, as in the brand reference — signing
      // in is the action an anonymous visitor is here to take.
      // `whitespace-nowrap`: a two-line label overflows the fixed-height button,
      // which is what the Arabic label did at 375px before this.
      // The reference's `.signin`: no icon, 14px, 22px sides in a 40px box
      // (7px/12px on a phone, where the lockup needs the room — at 320px the
      // Arabic label is the widest thing in the row).
      <Button
        variant="primary"
        size="sm"
        className="whitespace-nowrap md:h-10 md:px-[22px]"
        asChild
      >
        <Link to="/login">{t('login.submit')}</Link>
      </Button>
    )
  }

  const name = session.user?.full_name || session.user?.email || session.user?.phone || ''

  // Every surface this user holds a role for.
  //
  // The current one is **not** filtered out any more. Before Phase 10 this was
  // a shortcut beside a nav that already showed them all; now it is the only
  // place roles appear, and omitting the active one leaves a user unable to see
  // what they hold. It is marked instead.
  // ⚠️ **Ops is excluded — it moved to the primary navigation on 30 August
  // 2026.** Leaving it here as well would put one destination in two menus,
  // which is the redundancy that change set out to remove: an admin should
  // find their console in exactly one place, and that place is the nav bar
  // where they can see it without opening anything.
  //
  // Filtered by id rather than by dropping the entry from `PORTALS`: the
  // registry is what mounts the route, lazy-loads the chunk and drives
  // `RequireRole`, so removing it there would unmount the console entirely.
  // This is a *navigation* decision, so it is made in the navigation.
  const surfaces = PORTALS.filter(
    (portal) => portal.id !== 'ops' && portal.roles.some((role) => session.hasRole(role)),
  )

  const isCurrent = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href)

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t('account.menu')}>
          <UserRound aria-hidden="true" />
          {/* Hidden on small screens: a long name pushes the rest of the header
              off, and the icon alone is still a labelled control. */}
          <span className="hidden max-w-32 truncate sm:inline">{name}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <div className="px-2.5 py-2">
          <p className="truncate text-sm font-medium">{name}</p>
          {session.user?.email ? (
            <p className="truncate text-xs text-fg-muted" dir="ltr">
              {session.user.email}
            </p>
          ) : null}
        </div>

        <DropdownMenuSeparator />
        {/* Profile first — it is the thing most users open this menu for.
            Both rows are live: profile since Phase 11, notification settings
            since Phase 17. */}
        <DropdownMenuItem onSelect={() => void navigate('/me/profile')}>
          <UserRound aria-hidden="true" />
          <span className="flex-1">{tc('nav.myProfile')}</span>
        </DropdownMenuItem>
        {/* ⚠️ **"Notification settings", not "Account settings".** The screen
            behind it sets notification preferences and nothing else — no
            password, no email, no language. A broader label here is a promise
            the page does not keep, and the person who opens it looking for
            their password leaves thinking the product lost it.

            It was a disabled "soon" row until Phase 17 landed. The route
            existed for a whole phase reachable *only* from inside the
            notification bell's dropdown, which is how a shipped screen stays
            invisible: every other `/me/` surface has a home in this menu, and
            this one had none. */}
        {/* Roadmap 18.2. ⚠️ **The row the note above was waiting for.** That
            comment said the person opening "Notification settings" looking for
            their password "leaves thinking the product lost it" — because
            there was no account screen to send them to. Now there is, and it
            is listed here rather than reachable only by typing the URL: a
            screen findable only from another screen is one nobody finds, which
            is exactly how `/ops/users` shipped invisible earlier in this
            phase. */}
        <DropdownMenuItem onSelect={() => void navigate('/me/account')}>
          <KeyRound aria-hidden="true" />
          <span className="flex-1">{tc('nav.accountSettings')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void navigate('/me/notifications')}>
          <Settings aria-hidden="true" />
          <span className="flex-1">{tc('nav.notificationSettings')}</span>
        </DropdownMenuItem>

        {surfaces.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <p className="px-2.5 pt-1 pb-1.5 text-xs font-medium tracking-caps text-fg-subtle">
              {tc('nav.roleSurfaces')}
            </p>
            {surfaces.map((portal) => {
              const Icon = portal.icon
              const current = isCurrent(portal.href)
              return (
                <DropdownMenuItem
                  key={portal.id}
                  onSelect={() => void navigate(portal.href)}
                  aria-current={current ? 'page' : undefined}
                >
                  <Icon aria-hidden="true" />
                  <span className="flex-1">{tc(portal.labelKey)}</span>
                  {/* A dot, not a colour change: the active row must be
                      distinguishable without relying on hue (NFR-11).
                      ⚠️ `brand-fg`, not `brand-600`: an indicator owes 3:1 to
                      the menu behind it, and dark 600 is 2.26:1 on raised. */}
                  {current ? (
                    <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-fg" />
                  ) : null}
                </DropdownMenuItem>
              )
            })}
          </>
        ) : null}

        {/* Dark mode (Task 5, 1 October 2026: light by default, dark on
            request). On every width, unlike the language below — the footer
            has a switch too, but someone looking for a preference opens the
            menu that holds their other preferences. A checkbox row, so the
            state reads as a check rather than as a label that flips.
            ⚠️ `preventDefault` keeps the menu open: the whole page changes
            under it, and closing as well makes the toggle feel like a
            navigation. */}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={theme === 'dark'}
          onCheckedChange={toggleTheme}
          onSelect={(event) => event.preventDefault()}
        >
          <Moon aria-hidden="true" />
          <span className="flex-1">{tc('theme.dark')}</span>
        </DropdownMenuCheckboxItem>

        {/* Language sits in the menu on small screens, where the header has no
            room for the inline switcher. Above `md` both would show, so this
            copy hides itself. */}
        <div className="md:hidden">
          <DropdownMenuSeparator />
          <p className="px-2.5 pt-1 pb-1.5 text-xs font-medium tracking-caps text-fg-subtle">
            {tc('nav.language')}
          </p>
          <div className="px-2.5 pb-1.5">
            <LanguageSwitcher />
          </div>
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            logout.mutate(undefined, {
              // Home, not the login page: signing out of a portal should leave
              // the visitor on the public catalogue, not at a form.
              onSettled: () => void navigate('/', { replace: true }),
            })
          }}
        >
          <LogOut aria-hidden="true" />
          {t('logout.action')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}
